import { logger } from '@/utils/logger';

import { supabase } from '@/integrations/supabase/client';
import { verifyOrganization } from './organizationUtils';

export const handleLogin = async (
  email: string,
  password: string,
  organizationCode: string,
  signIn: (email: string, password: string) => Promise<{ error: any }>,
  setUserProfile: (profile: any) => void,
  setCurrentOrganization: (org: any) => void,
  setTwoFactorVerified: (verified: boolean) => void,
  toast: any
) => {
  logger.debug('Starting login process...');

  // First verify the organization
  const organization = await verifyOrganization(organizationCode);
  if (!organization) {
    throw new Error('Invalid organization code');
  }

  logger.debug('Organization verified:', organization.name);
  setCurrentOrganization(organization);

  // Attempt to sign in
  const { error } = await signIn(email, password);
  if (error) {
    logger.error('Sign in error:', error);
    throw error;
  }

  const { data, error: activeOrgError } = await supabase.functions.invoke('set-active-organization', {
    body: { organization_id: organization.id }
  });

  if (activeOrgError || data?.error) {
    await supabase.auth.signOut();
    throw new Error(data?.error || activeOrgError?.message || 'Unable to activate organization');
  }

  await supabase.auth.refreshSession();
  setUserProfile(data.membership);

  // Check if 2FA is enabled
  if (data.membership?.two_factor_enabled) {
    logger.debug('2FA is enabled, requiring verification');
    return { requires2FA: true };
  } else {
    logger.debug('2FA not enabled, setting as verified');
    setTwoFactorVerified(true);
    
    toast({
      title: "Welcome back!",
      description: `Signed in successfully to ${organization.name}`
    });
    
    return { requires2FA: false };
  }
};

export const handleSignup = async (
  email: string,
  password: string,
  fullName: string,
  organizationCode: string,
  signUp: (email: string, password: string, fullName?: string, organizationId?: string) => Promise<{ error: any }>,
  toast: any
) => {
  logger.debug('Starting signup process...');

  // First verify the organization exists
  const organization = await verifyOrganization(organizationCode);
  if (!organization) {
    throw new Error('Invalid organization code');
  }

  logger.debug('Organization verified for signup:', organization.name);

  const { error } = await signUp(email, password, fullName, organization.id);
  if (error) {
    logger.error('Signup error:', error);
    throw error;
  }

  toast({
    title: "Account created successfully!",
    description: `Please check your email to verify your account for ${organization.name}.`
  });
};

export const handle2FAVerification = async (
  twoFactorCode: string,
  userProfile: any,
  currentOrganization: any,
  verify2FA: (code: string, profile: any) => Promise<boolean>,
  setTwoFactorVerified: (verified: boolean) => void,
  toast: any
) => {
  logger.debug('Starting 2FA verification...');

  const isValid = await verify2FA(twoFactorCode, userProfile);
  if (!isValid) {
    throw new Error('Invalid 2FA code. Please try again.');
  }

  logger.debug('2FA verification successful');
  setTwoFactorVerified(true);
  
  toast({
    title: "Welcome back!",
    description: `Signed in successfully to ${currentOrganization?.name || 'your organization'}`
  });
};
